import { useEffect, useRef, useState } from 'react';
import { checkDeviceLocation } from './deviceLocationCheck';

export const deviceLocationMessages = {
  available: 'Device location is available. This check did not save or share your coordinates. Permission may change; each activity still needs a fresh fix.',
  denied: 'Location permission denied. If no prompt appears, allow location for this app or site in device settings, then retry. Older installed Android apps may need an app update before Location permission is available.',
  unavailable: 'Position unavailable. The device could not obtain a fix. Check location settings and try outdoors.',
  timeout: 'Location request timed out. Try again where the device can get a clearer signal.',
  unsupported: 'Location API unavailable in this environment. Use a supported browser over HTTPS or the installed app.',
  unknown: 'Could not verify device location. Check permissions and retry.'
};

// Automatic callers must first verify explicit app Position or capture consent. The
// WebView geolocation API uses Capacitor's native foreground permission bridge.
export default function useDeviceLocationCheck(scope, owner, enabled = true) {
  const mounted = useRef(false);
  const generation = useRef(0);
  const pending = useRef(null);
  const [checking, setChecking] = useState(false);
  const [status, setStatus] = useState(null);
  const reset = () => { generation.current += 1; setStatus(null); };
  useEffect(() => {
    mounted.current = true;
    generation.current += 1;
    pending.current = null;
    setChecking(false);
    setStatus(null);
    return () => { mounted.current = false; generation.current += 1; pending.current = null; };
  }, [scope, owner, enabled]);
  const request = async () => {
    if (pending.current) return;
    const token = {};
    pending.current = token;
    const current = generation.current;
    setChecking(true);
    try {
      const result = await checkDeviceLocation();
      if (mounted.current && current === generation.current) setStatus(result);
    } finally {
      if (pending.current === token) pending.current = null;
      if (mounted.current && pending.current === null && current === generation.current) setChecking(false);
    }
  };
  return { checking, status, request, reset };
}
